import { useState } from "react";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { ResumeIndicatorB3ByHealthTeamData } from "@/services/esus/indicator/esb/b3/getResumeIndicatorB3ByHealthTeam";
import {
	getIndicatorClassification,
	getIndicatorClassificationColorClassName,
	getIndicatorClassificationScores,
	IndicatorEsbEnum,
} from "@/utils/indicatorsEsb";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsbB3HealthTeamsClassificationTableColumns = {
	mainHealthTeamName: Column;
	linkedHealthTeamName: Column;
	totalToothExtractionProceduresLinked: Column;
	totalToothExtractionProceduresNotLinked: Column;
	totalProceduresLinked: Column;
	totalProceduresNotLinked: Column;
	classificationPercentageLinked: Column;
	classificationLinked: Column;
	classificationPercentage: Column;
	classification: Column;
};

type IndicatorEsbB3HealthTeamsTableProps = {
	healthTeamsClassifications: ResumeIndicatorB3ByHealthTeamData[];
	isLoading: boolean;
};

const indicatorClassificationScores = getIndicatorClassificationScores(IndicatorEsbEnum.B3);

export function IndicatorEsbB3HealthTeamsClassificationTable({
	healthTeamsClassifications,
	isLoading,
}: Readonly<IndicatorEsbB3HealthTeamsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsbB3HealthTeamsClassificationTableColumns>({
		mainHealthTeamName: { title: "Equipe ESB", visible: true },
		linkedHealthTeamName: { title: "Equipe ESF", visible: true },
		totalToothExtractionProceduresLinked: {
			title: "Exodontias em cidadãos vinculados",
			visible: true,
		},
		totalToothExtractionProceduresNotLinked: {
			title: "Exodontias em cidadãos não vinculados",
			visible: true,
		},
		totalProceduresLinked: { title: "Procedimentos em cidadãos vinculados", visible: true },
		totalProceduresNotLinked: {
			title: "Procedimentos em cidadãos não vinculados",
			visible: true,
		},
		classificationPercentageLinked: { title: "% Classificação cidadãos vinculados", visible: false },
		classificationLinked: { title: "Classificação cidadãos vinculados", visible: false },
		classificationPercentage: { title: "% Classificação", visible: true },
		classification: { title: "Classificação", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th
						className={`text-start px-2 ${
							!columns.mainHealthTeamName.visible && "d-none"
						}`}
					>
						{columns.mainHealthTeamName.title}
					</th>
					<th
						className={`text-start px-2 ${
							!columns.linkedHealthTeamName.visible && "d-none"
						}`}
					>
						{columns.linkedHealthTeamName.title}
					</th>
					<th
						className={`px-2 ${
							!columns.totalToothExtractionProceduresLinked.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.totalToothExtractionProceduresLinked.title}
							<CardOverlayTrigger
								header="Exodontias em cidadãos vinculados"
								message="Quantidade de procedimentos de exodontia realizados no período em cidadãos vinculados a equipes ESF que possuem vínculo com a ESB do atendimento"
								icon={<FaInfoCircle />}
							/>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.totalToothExtractionProceduresNotLinked.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.totalToothExtractionProceduresNotLinked.title}
							<CardOverlayTrigger
								header="Exodontias em cidadãos não vinculados"
								message="Quantidade de procedimentos de exodontia realizados no período em cidadãos vinculados a equipes ESF que não possuem vínculo com a ESB do atendimento"
								icon={<FaInfoCircle />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.totalProceduresLinked.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.totalProceduresLinked.title}
							<CardOverlayTrigger
								header="Procedimentos em cidadãos vinculados"
								message="Quantidade de procedimentos preventivos, curativos e exodontias realizados no período em cidadãos vinculados a equipes ESF que possuem vínculo com a ESB do atendimento"
								icon={<FaInfoCircle />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.totalProceduresNotLinked.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.totalProceduresNotLinked.title}
							<CardOverlayTrigger
								header="Procedimentos em cidadãos não vinculados"
								message="Quantidade de procedimentos preventivos, curativos e exodontias realizados no período em cidadãos vinculados a equipes ESF que não possuem vínculo com a ESB do atendimento"
								icon={<FaInfoCircle />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.classificationPercentageLinked.visible && "d-none"}`}>
						{columns.classificationPercentageLinked.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								Percentual de exodontias em cidadãos vinculados sobre <br />
								a quantidade de procedimentos em cidadãos vinculados
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.classificationLinked.visible && "d-none"}`}>
						{columns.classificationLinked.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								<div className="text-primary">{`Ótimo: >= ${indicatorClassificationScores.limit}% e < ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-success">{`Bom: >= ${indicatorClassificationScores.awesome}% e < ${indicatorClassificationScores.good}%`}</div>
								<br />
								<div className="text-warning">
									{`Suficiente: >= ${indicatorClassificationScores.good}% e < ${indicatorClassificationScores.enough}%`}
								</div>
								<br />
								<div className="text-danger">{`Regular: < ${indicatorClassificationScores.limit}% ou >= ${indicatorClassificationScores.enough}%`}</div>
								<br />
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.classificationPercentage.visible && "d-none"}`}>
						{columns.classificationPercentage.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								Percentual de exodontias em cidadãos vinculados e não vinculados sobre <br />
								a quantidade de procedimentos em cidadãos vinculados e não vinculados
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.classification.visible && "d-none"}`}>
						{columns.classification.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								<div className="text-primary">{`Ótimo: >= ${indicatorClassificationScores.limit}% e < ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-success">{`Bom: >= ${indicatorClassificationScores.awesome}% e < ${indicatorClassificationScores.good}%`}</div>
								<br />
								<div className="text-warning">
									{`Suficiente: >= ${indicatorClassificationScores.good}% e < ${indicatorClassificationScores.enough}%`}
								</div>
								<br />
								<div className="text-danger">{`Regular: < ${indicatorClassificationScores.limit}% ou >= ${indicatorClassificationScores.enough}%`}</div>
								<br />
							</div>
						</CardOverlayTrigger>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsbB3HealthTeamsClassificationTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsbB3HealthTeamsClassificationTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsbB3HealthTeamsClassificationTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && healthTeamsClassifications.length > 0
					? healthTeamsClassifications.map((healthTeamClassification, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.mainHealthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{healthTeamClassification.esbHealthTeamName}</div>
										<div className="text-muted">
											{healthTeamClassification.esbHealthTeamIne}
										</div>
									</div>
								</td>
								<td
									className={`text-start px-2 ${
										!columns.linkedHealthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{healthTeamClassification.linkedHealthTeamName}</div>
										<div className="text-muted">
											{healthTeamClassification.linkedHealthTeamIne}
										</div>
									</div>
								</td>
								<td
									className={`px-2 ${
										!columns.totalToothExtractionProceduresLinked.visible &&
										"d-none"
									}`}
								>
									{healthTeamClassification.totalToothExtractionProceduresLinked ??
										0}
								</td>
								<td
									className={`px-2 ${
										!columns.totalToothExtractionProceduresLinked.visible &&
										"d-none"
									}`}
								>
									{healthTeamClassification.totalToothExtractionProcedures -
										(healthTeamClassification.totalToothExtractionProceduresLinked ??
											0)}
								</td>
								<td
									className={`px-2 ${
										!columns.totalProceduresLinked.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalProceduresLinked ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.totalProceduresLinked.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalProcedures -
										(healthTeamClassification.totalProceduresLinked ?? 0)}
								</td>
								<td
									className={`px-2 ${
										!columns.classificationPercentageLinked.visible && "d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalToothExtractionProceduresLinked,
										healthTeamClassification.totalProceduresLinked
									)}%`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.classificationLinked.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEsbEnum.B3,
											calculatePercentage(
												healthTeamClassification.totalToothExtractionProceduresLinked,
												healthTeamClassification.totalProceduresLinked
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEsbEnum.B3,
											calculatePercentage(
												healthTeamClassification.totalToothExtractionProceduresLinked,
												healthTeamClassification.totalProceduresLinked
											)
										)}
									</Badge>
								</td>
								<td
									className={`px-2 ${
										!columns.classificationPercentage.visible && "d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalToothExtractionProcedures,
										healthTeamClassification.totalProcedures
									)}%`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.classification.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEsbEnum.B3,
											calculatePercentage(
												healthTeamClassification.totalToothExtractionProcedures,
												healthTeamClassification.totalProcedures
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEsbEnum.B3,
											calculatePercentage(
												healthTeamClassification.totalToothExtractionProcedures,
												healthTeamClassification.totalProcedures
											)
										)}
									</Badge>
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
